import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface UseTimerOptions {
  endTime: moment.Moment;
  format?: string;
  onComplete?: () => void;
}

interface UseTimerReturn {
  timeRemaining: string;
  remainingSeconds: number;
  start: () => void;
  stop: () => void;
  isRunning: boolean;
}

const DEFAULT_FORMAT = "HH:mm:ss";

export const useTimer = ({ endTime, format = DEFAULT_FORMAT, onComplete }: UseTimerOptions): UseTimerReturn => {
  const { t } = useTranslation();
  const [timeRemaining, setTimeRemaining] = useState<string>("00:00:00");
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const clockTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onCompleteRef = useRef(onComplete);
  const prevRemainingSecondsRef = useRef<number>(0);
  const remainingSecondsRef = useRef<number>(0);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    remainingSecondsRef.current = remainingSeconds;
  }, [remainingSeconds]);

  const calculateTimeRemaining = useCallback((): { formatted: string; seconds: number } => {
    const differenceSeconds = Math.ceil(endTime.diff(moment(), "seconds"));

    if (differenceSeconds <= 0) {
      if (onCompleteRef.current && prevRemainingSecondsRef.current > 0) {
        onCompleteRef.current();
      }
      return { formatted: "00:00:00", seconds: 0 };
    }

    const duration = moment.duration(differenceSeconds, "seconds");
    const days = Math.floor(duration.asDays());
    const dayPrefix = days > 0 ? `${t("days short", { count: days, defaultValue: "{{count}}D" })} ` : "";
    const formatted = `${dayPrefix}${moment.utc(duration.asMilliseconds()).format(format)}`;
    return { formatted, seconds: differenceSeconds };
  }, [endTime, format, t]);

  const updateTimer = useCallback(() => {
    const result = calculateTimeRemaining();
    prevRemainingSecondsRef.current = remainingSecondsRef.current;
    setTimeRemaining(result.formatted);
    setRemainingSeconds(result.seconds);
  }, [calculateTimeRemaining]);

  const start = useCallback(() => {
    if (intervalRef.current || clockTimeoutRef.current) {
      return;
    }

    updateTimer();
    // sync with machine clock at the first delay
    clockTimeoutRef.current = setTimeout(() => {
      updateTimer();
      intervalRef.current = setInterval(() => {
        updateTimer();
      }, 1000);
    }, 1000 - new Date().getMilliseconds());
  }, [updateTimer]);

  const stop = useCallback(() => {
    if (clockTimeoutRef.current) {
      clearTimeout(clockTimeoutRef.current);
      clockTimeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  useEffect(() => {
    const result = calculateTimeRemaining();
    setTimeRemaining(result.formatted);
    setRemainingSeconds(result.seconds);
  }, [endTime, calculateTimeRemaining]);

  const isRunning = intervalRef.current !== null || clockTimeoutRef.current !== null;

  return {
    timeRemaining,
    remainingSeconds,
    start,
    stop,
    isRunning,
  };
};
